<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import Auth from 'dashboard/api/auth';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useSipValidation } from '../composables/useSipValidation';

// Create authenticated axios instance
const authAxios = axios.create();
if (Auth.hasAuthCookie()) {
  const authData = Auth.getAuthData();
  Object.assign(authAxios.defaults.headers.common, {
    'access-token': authData['access-token'],
    'token-type': authData['token-type'],
    client: authData.client,
    expiry: authData.expiry,
    uid: authData.uid,
  });
}

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const route = useRoute();

// Get account_id from route
const accountId = computed(() => route.params.accountId);

// Form state
const sipServer = ref('');
const sipPort = ref('');
const sipTransport = ref('wss');
const sipRealm = ref('');
const registrationMode = ref('per_agent');
const isSaving = ref(false);

// Initialize form values from inbox
const initializeForm = () => {
  console.log('[PLUGIN][SIP-CHANNEL][Settings] Initializing form with inbox:', props.inbox);
  console.log('[PLUGIN][SIP-CHANNEL][Settings] provider_config:', props.inbox.provider_config);
  
  const config = props.inbox.provider_config || {};
  sipServer.value = config.sip_server || '';
  sipPort.value = config.sip_port?.toString() || '';
  sipTransport.value = config.sip_transport || 'wss';
  sipRealm.value = config.sip_realm || '';
  registrationMode.value = config.registration_mode || 'per_agent';
  
  console.log('[PLUGIN][SIP-CHANNEL][Settings] Form initialized:', {
    sipServer: sipServer.value,
    sipPort: sipPort.value,
    sipTransport: sipTransport.value,
    sipRealm: sipRealm.value,
    registrationMode: registrationMode.value,
  });
};

// Watch for inbox changes
watch(() => props.inbox, initializeForm, { immediate: true, deep: true });

// SIP validation
const { isValid, errorMessage, validateConnection } = useSipValidation(
  sipServer,
  sipPort,
  sipTransport
);

// Registration mode options
const registrationModeOptions = computed(() => [
  {
    label: 'Registro por Agente (cada agente tem seu ramal)',
    value: 'per_agent',
  },
  {
    label: 'Único Registro Compartilhado (todos agentes usam mesmo ramal)',
    value: 'shared',
  },
]);

// Transport options
const transportOptions = [
  { label: 'WSS (Seguro)', value: 'wss' },
  { label: 'WS', value: 'ws' },
];

// Help text based on mode
const registrationModeHelp = computed(() => {
  if (registrationMode.value === 'per_agent') {
    return 'The administrator will configure the extension and password for each agent in the inbox settings';
  }
  return 'Calls will be distributed automatically - first to answer gets the call';
});

// Save configuration
const saveConfiguration = async () => {
  isSaving.value = true;

  try {
    const payload = {
      id: props.inbox.id,
      formData: false,
      channel: {
        sip_server: sipServer.value,
        sip_port: sipPort.value ? parseInt(sipPort.value, 10) : null,
        sip_transport: sipTransport.value,
        sip_realm: sipRealm.value || sipServer.value,
        registration_mode: registrationMode.value,
      },
    };

    // Update via custom SIP endpoint
    const response = await authAxios.patch(
      `/api/v1/accounts/${accountId.value}/sip_inboxes/${props.inbox.id}`,
      payload
    );

    if (response.status !== 200) {
      throw new Error('Failed to update SIP configuration');
    }

    useAlert('SIP settings updated successfully!');
  } catch (error) {
    // eslint-disable-next-line no-console
    useAlert('Error updating SIP settings');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsSection
    title="SIP Server Configuration"
    sub-title="Configure connection properties for the SIP server"
  >
    <form class="space-y-4" @submit.prevent="saveConfiguration">
        <!-- SIP Server -->
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            SIP Server
          </span>
          <input
            v-model="sipServer"
            type="text"
            placeholder="sip.example.com"
            class="w-full rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            required
            @blur="validateConnection"
          />
          <span class="text-xs text-slate-600 dark:text-slate-400">
            SIP server address (hostname or IP)
          </span>
        </label>

        <!-- SIP Port -->
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            SIP Port (optional)
          </span>
          <input
            v-model="sipPort"
            type="number"
            min="1"
            max="65535"
            placeholder="Default: 8089 (WSS) / 8088 (WS)"
            class="w-full rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
          <span class="text-xs text-slate-600 dark:text-slate-400">
            Server port (leave empty to use the transport default)
          </span>
        </label>

        <!-- Transport Protocol -->
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            Transport Protocol
          </span>
          <select
            v-model="sipTransport"
            class="w-full rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            @change="validateConnection"
          >
            <option
              v-for="option in transportOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span class="text-xs text-slate-600 dark:text-slate-400">
            Protocol used to connect to the server
          </span>
        </label>

        <!-- SIP Realm -->
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            SIP Domain (Realm)
          </span>
          <input
            v-model="sipRealm"
            type="text"
            placeholder="Default: same as server"
            class="w-full rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
          <span class="text-xs text-slate-600 dark:text-slate-400">
            Authentication domain (leave empty to use same as server)
          </span>
        </label>

        <!-- Registration Mode -->
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            Registration Mode
          </span>
          <select
            v-model="registrationMode"
            class="w-full rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          >
            <option
              v-for="option in registrationModeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span class="text-xs text-slate-600 dark:text-slate-400">
            {{ registrationModeHelp }}
          </span>
        </label>

        <!-- Connection Status -->
        <div
          v-if="errorMessage"
          class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3"
        >
          <p class="text-sm text-red-700 dark:text-red-300">
            ⚠️ {{ errorMessage }}
          </p>
        </div>

        <div
          v-else-if="isValid && sipServer"
          class="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3"
        >
          <p class="text-sm text-green-700 dark:text-green-300">
            ✓ Conexão validada com sucesso
          </p>
        </div>

        <!-- Save Button -->
        <div class="flex justify-end pt-2">
          <NextButton
            type="submit"
            :disabled="isSaving || !sipServer"
            :loading="isSaving"
          >
            Save Settings
          </NextButton>
        </div>
      </form>
    </SettingsSection>
</template>
